import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';
import { isAdminTabUrl } from '../services/admin-tabs.utils';
import { isTabAware } from '../services/tab-aware';

/**
 * Multi-key cache of admin route component trees, keyed by tab id from
 * AdminTabsService. Lets up to N tabs keep their rendered state alive across
 * navigation — switching tabs is a DOM detach/insert, not a re-mount.
 *
 * When a route is detached, walks the ComponentRef tree and calls
 * `onTabDetach()` on any provider implementing TabAware so subscriptions can
 * pause. `onTabAttach()` fires on retrieve.
 *
 * Routes can opt out by setting `data: { retain: false }` on the route config.
 */
@Injectable({ providedIn: 'root' })
export class AdminRouteReuseStrategy implements RouteReuseStrategy {
  private readonly cache = new Map<string, DetachedRouteHandle>();
  /** Tab id at the moment of detach — needed because the active id changes
   *  between detach and store. */
  private pendingDetachTabId: string | null = null;

  /** Callback set by AdminTabsService after construction — avoids a circular
   *  dependency (AdminTabsService → Router → RouteReuseStrategy → this). */
  private getActiveTabId: () => string | null = () => null;

  /** Wired by AdminTabsService.constructor */
  setActiveTabIdProvider(provider: () => string | null): void {
    this.getActiveTabId = provider;
  }

  /** True if this route belongs to the admin tabs system. */
  isAdminRoute(route: ActivatedRouteSnapshot): boolean {
    const url = '/' + route.pathFromRoot
      .map(r => r.url.map(s => s.path).join('/'))
      .filter(Boolean)
      .join('/');
    if (!isAdminTabUrl(url)) return false;
    if (route.data?.['retain'] === false) return false;
    // Skip componentless parent routes (e.g. /settings/catering with redirectTo).
    // We only manage detach/attach for routes that actually render a component;
    // otherwise we'd attach the wrong cached view into a parent without an outlet.
    if (!this.hasComponent(route)) return false;
    return true;
  }

  private hasComponent(route: ActivatedRouteSnapshot): boolean {
    const cfg = route.routeConfig;
    if (!cfg) return false;
    return !!cfg.component || !!(cfg as any).loadComponent;
  }

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    if (!this.isAdminRoute(route)) return false;
    // Only detach when there is an active tab to key the cache by.
    const active = this.getActiveTabId();
    if (!active) return false;
    this.pendingDetachTabId = active;
    return true;
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    const key = this.pendingDetachTabId;
    this.pendingDetachTabId = null;
    if (!key) return;
    if (handle === null) {
      this.cache.delete(key);
      return;
    }
    // Notify the component that it's about to be hidden.
    this.notify(handle, 'detach');
    this.cache.set(key, handle);
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    if (!this.isAdminRoute(route)) return false;
    const active = this.getActiveTabId();
    if (!active) return false;
    if (!this.cache.has(active)) return false;
    // Verify the cached handle is for the same route component (otherwise
    // we'd attach the wrong page when the same tab navigates between routes).
    const handle = this.cache.get(active)!;
    const cachedComponent = (handle as any)?.componentRef?.componentType ?? null;
    const cfg = route.routeConfig as any;
    const routeComponent = cfg?.component ?? cfg?._loadedComponent ?? null;
    if (!cachedComponent || !routeComponent) return false;
    return cachedComponent === routeComponent;
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    if (!this.isAdminRoute(route)) return null;
    const active = this.getActiveTabId();
    if (!active) return null;
    const handle = this.cache.get(active) ?? null;
    if (handle) this.notify(handle, 'attach');
    return handle;
  }

  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    return future.routeConfig === curr.routeConfig;
  }

  /** Drop a cached entry — called by AdminTabsService when a tab is closed. */
  evict(tabId: string): void {
    const handle = this.cache.get(tabId);
    if (!handle) return;
    // Best-effort destroy — ComponentRef sits at a private field. If we can't
    // reach it, dropping the reference lets the GC pick it up eventually.
    try {
      const cmpRef = (handle as any)?.componentRef;
      cmpRef?.destroy?.();
    } catch {
      /* swallow */
    }
    this.cache.delete(tabId);
  }

  /** Walk the component tree under a detached handle and call TabAware hooks. */
  private notify(handle: DetachedRouteHandle, phase: 'detach' | 'attach'): void {
    const componentRef = (handle as any)?.componentRef;
    const instance = componentRef?.instance;
    if (!instance) return;
    if (isTabAware(instance)) {
      try {
        if (phase === 'detach') instance.onTabDetach?.();
        else instance.onTabAttach?.();
      } catch (err) {
        console.warn(`[AdminRouteReuseStrategy] TabAware ${phase} threw:`, err);
      }
    }
  }
}
